import { z } from "zod";

import type { PortableBoardSnapshotV1 } from "./portable-snapshot";

export const snapshotCompareEntityTypeSchema = z.enum([
  "board",
  "list",
  "card",
  "checklist",
  "custom_field",
  "label",
  "member",
  "membership",
  "attachment",
  "comment",
  "activity",
]);
export type SnapshotCompareEntityType = z.infer<
  typeof snapshotCompareEntityTypeSchema
>;

const snapshotChangeCountSchema = z.object({
  added: z.int().nonnegative(),
  changed: z.int().nonnegative(),
  moved: z.int().nonnegative(),
  removed: z.int().nonnegative(),
});

export const snapshotAttachmentComparisonOutcomeSchema = z.object({
  byteSize: z.int().nonnegative().nullable(),
  sha256: z
    .string()
    .regex(/^[a-f0-9]{64}$/)
    .nullable(),
  status: z.enum(["copied", "failed", "link_only", "skipped"]),
});
export type SnapshotAttachmentComparisonOutcome = z.infer<
  typeof snapshotAttachmentComparisonOutcomeSchema
>;

export const snapshotCompareChangeSchema = z.object({
  attachmentOutcome: z
    .object({
      from: snapshotAttachmentComparisonOutcomeSchema.nullable(),
      to: snapshotAttachmentComparisonOutcomeSchema.nullable(),
    })
    .optional(),
  change: z.enum(["added", "changed", "moved", "removed"]),
  changedFields: z.array(z.string().min(1)).max(128),
  entityId: z.string().min(1).max(128),
  entityType: snapshotCompareEntityTypeSchema,
  label: z.string().min(1).max(512),
});
export type SnapshotCompareChange = z.infer<typeof snapshotCompareChangeSchema>;

export const cloudSnapshotCompareRequestSchema = z
  .object({
    fromSnapshotId: z.uuid(),
    toSnapshotId: z.uuid(),
  })
  .refine(
    ({ fromSnapshotId, toSnapshotId }) => fromSnapshotId !== toSnapshotId,
    { message: "Choose two different snapshots." },
  );
export type CloudSnapshotCompareRequest = z.infer<
  typeof cloudSnapshotCompareRequestSchema
>;

export const snapshotComparisonSchema = z.object({
  changes: z.array(snapshotCompareChangeSchema).max(50_000),
  collections: z.record(
    snapshotCompareEntityTypeSchema,
    snapshotChangeCountSchema,
  ),
  from: z.object({
    capturedAt: z.iso.datetime(),
    snapshotId: z.uuid(),
  }),
  to: z.object({
    capturedAt: z.iso.datetime(),
    snapshotId: z.uuid(),
  }),
  totalChanges: z.int().nonnegative(),
});
export type SnapshotComparison = z.infer<typeof snapshotComparisonSchema>;

type ComparableEntity = Record<string, unknown> & { id: string };

const entityTypes = snapshotCompareEntityTypeSchema.options;

function canonical(value: unknown): string {
  if (Array.isArray(value)) {
    return `[${value.map(canonical).join(",")}]`;
  }
  if (value !== null && typeof value === "object") {
    return `{${Object.entries(value)
      .toSorted(([left], [right]) => left.localeCompare(right))
      .map(([key, nested]) => `${JSON.stringify(key)}:${canonical(nested)}`)
      .join(",")}}`;
  }
  return JSON.stringify(value);
}

function normalizedEntity(
  type: SnapshotCompareEntityType,
  entity: ComparableEntity,
): ComparableEntity {
  if (type !== "card") {
    return entity;
  }
  return {
    ...entity,
    checklistIds: Array.isArray(entity.checklistIds)
      ? entity.checklistIds.toSorted()
      : entity.checklistIds,
    customFieldItems: Array.isArray(entity.customFieldItems)
      ? entity.customFieldItems.toSorted((left, right) =>
          canonical(left).localeCompare(canonical(right)),
        )
      : entity.customFieldItems,
    labelIds: Array.isArray(entity.labelIds)
      ? entity.labelIds.toSorted()
      : entity.labelIds,
    memberIds: Array.isArray(entity.memberIds)
      ? entity.memberIds.toSorted()
      : entity.memberIds,
    voterMemberIds: Array.isArray(entity.voterMemberIds)
      ? entity.voterMemberIds.toSorted()
      : entity.voterMemberIds,
  };
}

function changedFields(
  type: SnapshotCompareEntityType,
  before: ComparableEntity,
  after: ComparableEntity,
): string[] {
  const left = normalizedEntity(type, before);
  const right = normalizedEntity(type, after);
  return Array.from(new Set([...Object.keys(left), ...Object.keys(right)]))
    .filter(
      (field) =>
        field !== "id" && canonical(left[field]) !== canonical(right[field]),
    )
    .toSorted();
}

function entityLabel(
  type: SnapshotCompareEntityType,
  entity: ComparableEntity,
): string {
  const text = (field: string): string | null =>
    typeof entity[field] === "string" && entity[field] !== ""
      ? entity[field]
      : null;
  switch (type) {
    case "board":
    case "list":
    case "card":
    case "checklist":
    case "custom_field":
    case "label":
    case "attachment":
      return text("name") ?? `${type.replaceAll("_", " ")} ${entity.id}`;
    case "member":
      return text("fullName") ?? text("username") ?? `member ${entity.id}`;
    case "membership":
      return `membership ${text("memberId") ?? entity.id}`;
    case "comment":
      return `Comment on ${text("cardName") ?? text("cardId") ?? "card"}`;
    case "activity":
      return `${text("type") ?? "Activity"} on ${text("cardName") ?? text("cardId") ?? "card"}`;
  }
}

function compareCollection(
  type: SnapshotCompareEntityType,
  before: readonly ComparableEntity[],
  after: readonly ComparableEntity[],
  attachmentOutcomes?: {
    before: Readonly<Record<string, SnapshotAttachmentComparisonOutcome>>;
    after: Readonly<Record<string, SnapshotAttachmentComparisonOutcome>>;
  },
): SnapshotCompareChange[] {
  const left = new Map(before.map((entity) => [entity.id, entity]));
  const right = new Map(after.map((entity) => [entity.id, entity]));
  const changes: SnapshotCompareChange[] = [];

  for (const entityId of new Set([...left.keys(), ...right.keys()])) {
    const previous = left.get(entityId);
    const current = right.get(entityId);
    if (!previous && current) {
      changes.push({
        ...(type === "attachment"
          ? {
              attachmentOutcome: {
                from: null,
                to: attachmentOutcomes?.after[entityId] ?? null,
              },
            }
          : {}),
        change: "added",
        changedFields: [],
        entityId,
        entityType: type,
        label: entityLabel(type, current),
      });
      continue;
    }
    if (previous && !current) {
      changes.push({
        ...(type === "attachment"
          ? {
              attachmentOutcome: {
                from: attachmentOutcomes?.before[entityId] ?? null,
                to: null,
              },
            }
          : {}),
        change: "removed",
        changedFields: [],
        entityId,
        entityType: type,
        label: entityLabel(type, previous),
      });
      continue;
    }
    if (previous && current) {
      const fields = changedFields(type, previous, current);
      if (type === "attachment") {
        const priorOutcome = attachmentOutcomes?.before[entityId] ?? null;
        const currentOutcome = attachmentOutcomes?.after[entityId] ?? null;
        if (canonical(priorOutcome) !== canonical(currentOutcome)) {
          fields.push("captureOutcome");
        }
      }
      if (fields.length > 0) {
        const moved =
          (type === "card" &&
            (fields.includes("listId") || fields.includes("position"))) ||
          (type === "list" && fields.includes("position"));
        changes.push({
          ...(type === "attachment"
            ? {
                attachmentOutcome: {
                  from: attachmentOutcomes?.before[entityId] ?? null,
                  to: attachmentOutcomes?.after[entityId] ?? null,
                },
              }
            : {}),
          change: moved ? "moved" : "changed",
          changedFields: fields.toSorted(),
          entityId,
          entityType: type,
          label: entityLabel(type, current),
        });
      }
    }
  }
  return changes.toSorted(
    (leftChange, rightChange) =>
      leftChange.entityId.localeCompare(rightChange.entityId) ||
      leftChange.change.localeCompare(rightChange.change),
  );
}

function asEntities<T extends { id: string }>(
  entities: readonly T[],
): ComparableEntity[] {
  return entities as unknown as ComparableEntity[];
}

export function comparePortableSnapshots(
  before: PortableBoardSnapshotV1,
  after: PortableBoardSnapshotV1,
  options: {
    attachmentOutcomes?: {
      before: Readonly<Record<string, SnapshotAttachmentComparisonOutcome>>;
      after: Readonly<Record<string, SnapshotAttachmentComparisonOutcome>>;
    };
  } = {},
): SnapshotComparison {
  if (before.source.boardId !== after.source.boardId) {
    throw new RangeError("Snapshots must describe the same source board.");
  }
  const byType: Record<SnapshotCompareEntityType, SnapshotCompareChange[]> = {
    activity: compareCollection(
      "activity",
      asEntities(before.activities),
      asEntities(after.activities),
    ),
    attachment: compareCollection(
      "attachment",
      asEntities(before.attachments),
      asEntities(after.attachments),
      options.attachmentOutcomes,
    ),
    board: compareCollection(
      "board",
      asEntities([before.board]),
      asEntities([after.board]),
    ),
    card: compareCollection(
      "card",
      asEntities(before.cards),
      asEntities(after.cards),
    ),
    checklist: compareCollection(
      "checklist",
      asEntities(before.checklists),
      asEntities(after.checklists),
    ),
    comment: compareCollection(
      "comment",
      asEntities(before.comments),
      asEntities(after.comments),
    ),
    custom_field: compareCollection(
      "custom_field",
      asEntities(before.customFields),
      asEntities(after.customFields),
    ),
    label: compareCollection(
      "label",
      asEntities(before.labels),
      asEntities(after.labels),
    ),
    list: compareCollection(
      "list",
      asEntities(before.lists),
      asEntities(after.lists),
    ),
    member: compareCollection(
      "member",
      asEntities(before.members),
      asEntities(after.members),
    ),
    membership: compareCollection(
      "membership",
      asEntities(before.memberships),
      asEntities(after.memberships),
    ),
  };
  const changes = entityTypes
    .filter((type) => type !== "comment" && type !== "activity")
    .flatMap((type) => byType[type]);
  const collections = Object.fromEntries(
    entityTypes.map((type) => {
      const typeChanges = byType[type];
      return [
        type,
        {
          added: typeChanges.filter(({ change }) => change === "added").length,
          changed: typeChanges.filter(({ change }) => change === "changed")
            .length,
          moved: typeChanges.filter(({ change }) => change === "moved").length,
          removed: typeChanges.filter(({ change }) => change === "removed")
            .length,
        },
      ];
    }),
  );
  return snapshotComparisonSchema.parse({
    changes,
    collections,
    from: {
      capturedAt: before.capturedAt,
      snapshotId: before.snapshotId,
    },
    to: { capturedAt: after.capturedAt, snapshotId: after.snapshotId },
    totalChanges: entityTypes.reduce(
      (total, type) => total + byType[type].length,
      0,
    ),
  });
}
